/**
 * Custom hook for managing content state and operations
 */

import { useState, useEffect, useMemo } from 'react'
import { api } from '../../../../services'
import { message } from 'antd'

export const useContentManagement = ({
  tableOfContent,
  onTableOfContentChange,
  isFromKnowledgeCenter
}) => {
  const [content, setContent] = useState({})
  const [isAddingContentFromInternet, setIsAddingContentFromInternet] = useState(null)

  useEffect(() => {
    if (tableOfContent) {
      setContent(JSON.parse(tableOfContent.content))
    }
  }, [tableOfContent])

  const updateContent = newContent => {
    setContent(newContent)
    onTableOfContentChange({
      tableOfContent: JSON.stringify(newContent),
      id: tableOfContent.id
    })
  }

  const handleDelete = (section, subsection) => {
    const newContent = { ...content }
    if (!subsection) {
      delete newContent[section]
    } else {
      delete newContent[section][subsection]
    }
    updateContent(newContent)
  }

  const handleAddTopic = (currentSection, newTitle) => {
    if (newTitle && currentSection !== null) {
      const newContent = { ...content }

      if (!newContent[currentSection]) {
        newContent[currentSection] = {}
      }

      const currentSectionObject = newContent[currentSection]
      currentSectionObject[`${newTitle}`] = {}
      updateContent(newContent)
    }
  }

  const handleAddLesson = (newLessonTitle) => {
    if (newLessonTitle) {
      const newContent = { ...content }
      newContent[`${newLessonTitle}`] = {}
      updateContent(newContent)
    }
  }

  const handleSaveEdit = (currentSection, currentSubsection, newTitle) => {
    if (newTitle && currentSection !== null) {
      const newContent = { ...content }

      if (currentSubsection === null) {
        // Editing lesson title without changing the order
        const lessonKeys = Object.keys(newContent)
        const lessonIndex = lessonKeys.indexOf(currentSection)
        const lessonTopics = newContent[currentSection]

        if (lessonIndex !== -1) {
          lessonKeys[lessonIndex] = newTitle
        }

        const updatedContent = {}
        lessonKeys.forEach(key => {
          updatedContent[key] = newContent[key]
        })

        updatedContent[newTitle] = lessonTopics
        updateContent(updatedContent)
      } else {
        // Editing topic title
        const topics = { ...newContent[currentSection] }
        const updatedTopics = {}

        // Iterate through the topics, update the title if it matches currentSubsection
        Object.keys(topics).forEach(topic => {
          if (topic === currentSubsection) {
            updatedTopics[newTitle] = topics[topic]
          } else {
            updatedTopics[topic] = topics[topic]
          }
        })

        newContent[currentSection] = updatedTopics
        updateContent(newContent)
      }
    }
  }

  const handleAddContentFromInternet = (generatedContentModal, courseTitle, setGeneratedContentModal) => {
    const { content: generatedContent, title, lesson } = generatedContentModal

    setIsAddingContentFromInternet({
      topic: lesson,
      sub_topic: title
    })

    const payload = {
      course_title: courseTitle,
      topic: lesson,
      sub_topic: title,
      database_content: generatedContent
    }

    api.courses
      .generateContentFromInternet(payload)
      .then(res => {
        const text = res.data
        setGeneratedContentModal(pre => ({
          ...pre,
          content: text
        }))
        setContent(pre => ({
          ...pre,
          [lesson]: {
            ...pre[lesson],
            [title]: {
              generatedContent: text
            }
          }
        }))
      })
      .finally(() => {
        setIsAddingContentFromInternet(null)
      })
  }

  const progressStatus = useMemo(() => {
    if (!content) {
      return {
        totalSubtopics: 0,
        totalSubtopicsWithContent: 0,
        subTopicsWithError: 0
      }
    }

    // total subtopics inside the content
    const totalSubtopics = Object.keys(content).reduce((acc, section) => {
      return acc + Object.keys(content[section]).length
    }, 0)

    // total subtopics inside the content with generated content
    const totalSubtopicsWithContent = Object.keys(content).reduce(
      (acc, section) => {
        return (
          acc +
          Object.keys(content[section]).reduce((acc, subtopic) => {
            return acc + (content[section][subtopic].generatedContent ? 1 : 0)
          }, 0)
        )
      },
      0
    )

    const subTopicsWithError = totalSubtopics - totalSubtopicsWithContent

    return {
      totalSubtopics,
      totalSubtopicsWithContent,
      subTopicsWithError
    }
  }, [content])

  const isContentGeneratedSuccessfully = useMemo(() => {
    if (progressStatus.totalSubtopicsWithContent === 0) return false
    return (
      progressStatus.totalSubtopicsWithContent === progressStatus.totalSubtopics
    )
  }, [progressStatus])

  const contentSuccessInAllSections = useMemo(() => {
    const isSuccess = Object.keys(content).every(section =>
      Object.keys(content[section]).every(subsection => {
        const contentItem =
          content && content[section] && content[section][subsection]
        const generatedContent = contentItem && contentItem.generatedContent
        const status = contentItem && contentItem.status

        return generatedContent && status !== 'error'
      })
    )

    return isSuccess
  }, [content])

  const checkError = () => {
    return Object.values(content).some(section => {
      return Object.values(section).some(subsection => {
        return subsection.status === 'error'
      })
    })
  }

  return {
    content,
    setContent,
    updateContent,
    isAddingContentFromInternet,
    setIsAddingContentFromInternet,
    handleDelete,
    handleAddTopic,
    handleAddLesson,
    handleSaveEdit,
    handleAddContentFromInternet,
    progressStatus,
    isContentGeneratedSuccessfully,
    contentSuccessInAllSections,
    checkError
  }
}
